import { useState } from 'react';
import type { MetaAd } from '../types';
import { formatBRL, formatPercent, formatNumber } from '../lib/format';

type SortKey = 'spend' | 'impressions' | 'reach' | 'ctr' | 'cpm' | 'results';

interface Props {
  ads: MetaAd[];
}

export function AdTable({ ads }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function handleSort(key: SortKey) {
    if (key === sortKey) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  const sorted = [...ads].sort((a, b) => {
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  function th(label: string, key?: SortKey) {
    const isActive = key === sortKey;
    return (
      <th
        key={label}
        onClick={key ? () => handleSort(key) : undefined}
        style={{
          padding: '10px 14px',
          textAlign: key ? 'right' : 'left',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase' as const,
          color: isActive ? 'var(--accent)' : 'var(--text-tertiary)',
          fontFamily: 'var(--font-sans)',
          cursor: key ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}
      >
        {label}{isActive ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
      </th>
    );
  }

  if (ads.length === 0) {
    return (
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        fontSize: '13px',
      }}>
        Nenhum anúncio encontrado
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              {th('Anúncio')}
              {th('Gasto', 'spend')}
              {th('Alcance', 'reach')}
              {th('Impressões', 'impressions')}
              {th('CTR', 'ctr')}
              {th('CPM', 'cpm')}
              {th('Resultados', 'results')}
            </tr>
          </thead>
          <tbody>
            {sorted.map((ad, i) => (
              <tr
                key={`${ad.ad_id}_${i}`}
                style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={{ padding: '10px 14px', maxWidth: '240px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ad.ad_name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                    {ad.campaign_name}
                  </div>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>{formatBRL(ad.spend)}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>{formatNumber(ad.reach)}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>{formatNumber(ad.impressions)}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>{formatPercent(ad.ctr)}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>{formatBRL(ad.cpm)}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>{formatNumber(ad.results)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
